import { CoinData, CoinIcon, WalletMapping, Order } from 'app/types/api';
import { services } from 'app/services';
import { State } from 'app/store/types';
import { loadingActionBuilder } from 'app/store/modules/utils';

export type LoadingKey = 'coinInfo' | 'walletOverTime' | 'orders' | 'overall';

export type FetchCoinInfoAction = { type: 'api/FETCH_COIN_INFO'; payload: Array<CoinData> };
export type UpdateCoinInfo = { type: 'api/UPDATE_COIN_INFO'; payload: Array<CoinIcon> };
export type FetchWalletOverTimeAction = { type: 'api/FETCH_WALLET_OVER_TIME'; payload: Array<WalletMapping> };
export type FetchOrdersAction = { type: 'api/FETCH_ORDERS'; payload: Array<Order> };
export type LoadingAction = { type: 'api/LOADING'; payload: { key: LoadingKey; isLoading: boolean } };

export type ApiActions =
  FetchCoinInfoAction | FetchWalletOverTimeAction | FetchOrdersAction | LoadingAction | UpdateCoinInfo;

export const loadingAction = loadingActionBuilder<'api/LOADING', LoadingKey>('api/LOADING');

function wire<Payload>(
  key: LoadingKey,
  serviceCall: (getState: () => State) => Promise<Payload>,
  actionBuilder: (payload: Payload) => ApiActions
): (dispatch: Dispatch, getState: () => State) => Promise<void> {
  return async (dispatch, getState): Promise<void> => {
    dispatch(loadingAction(key, { isLoading: true }));
    try {
      const result = await serviceCall(getState);
      dispatch(actionBuilder(result));
    } catch (e) {
      console.log(e);
    }
    dispatch(loadingAction(key, { isLoading: false }));
  };
}

export const setLoadingOverall = (payload: { isLoading: boolean }) => loadingAction('overall', payload);

/**
 * This used to merge /api/v1/coin_info in ahead of the coingecko catalogue, since
 * cryptocompare supplied nicer names and the icon urls. That endpoint has needed an
 * api key for a while now and the backend answers it with an empty list, so it
 * contributed nothing but a request and a way for the whole load to fail: both
 * sources shared a Promise.all, so a throwing coin_info would have taken the
 * catalogue down with it and left every coin nameless.
 *
 * Names come from the catalogue and icons from coins/markets via fetchCoinIcons.
 */
export const fetchCoinInfo = () =>
  wire(
    'coinInfo',
    async () => {
      const geckoList = await services.coinGeckoService.getCoinList();

      return geckoList.map(({ name, symbol }) => ({
        symbol: symbol.toUpperCase(),
        name,
        imageUrl: undefined,
      }));
    },
    payload => ({ type: 'api/FETCH_COIN_INFO', payload })
  );

export const fetchWalletOverTime = () =>
  wire(
    'walletOverTime',
    () => services.apiService.getWalletOverTime(),
    payload => ({ type: 'api/FETCH_WALLET_OVER_TIME', payload })
  );

export const fetchOrders = () =>
  wire(
    'orders',
    getState => {
      const {
        auth: { userId, authToken },
      } = getState();
      if (userId && authToken) return services.apiService.getOrders(userId, authToken);
      else throw new Error('not logged in');
    },
    payload => ({ type: 'api/FETCH_ORDERS', payload })
  );

/**
 * cryptocompare's coinlist — where /api/v1/coin_info sourced icons from — now needs
 * an api key and answers 401, so coin_info comes back empty and nothing has an icon.
 * coins/markets gives us the same thing from coingecko without a key.
 *
 * Only ever called for coins the user actually holds, since coins/markets takes the
 * same 50-symbols-per-request cap and the full coin list runs to tens of thousands.
 */
export const fetchCoinIcons =
  () =>
  async (dispatch: Dispatch, getState: () => State): Promise<void> => {
    const {
      balance: { trackedCoins },
    } = getState();

    if (trackedCoins.length === 0) return;

    try {
      const markets = await services.coinGeckoService.getMarkets(trackedCoins);

      dispatch({
        type: 'api/UPDATE_COIN_INFO',
        payload: markets.map(({ symbol, image }) => ({ symbol: symbol.toUpperCase(), imageUrl: image })),
      });
    } catch (e) {
      // icons are decoration; never let them take the page down with them
      console.warn('could not load coin icons', e);
    }
  };
